import React, {ReactNode} from "react";
import {connect} from "react-redux";
import {Nullable} from "@data-force/app";
import "./TasksWizard.scss";
import {Spin} from "antd";
import StoreState from "../../../interfaces/store/storeState";
import {
    Status, StatusStats, Task, TaskEntity, TaskMaskData,
} from "../../../interfaces/store/actions/payload/task";
import TaskContainer from "../TaskContainer/TaskContainer";
import FilePicker from "../../FilePicker/FilePicker";
import {selectTaskInWizard, updateTaskStatus, setTaskMaskData} from "../../../store/actions/task";
import {
    SelectTaskInWizardAction,
    SetTaskMaskDataAction,
    UpdateTaskStatusAction,
} from "../../../interfaces/store/actions/task";
import HistoryParams from "../../../interfaces/historyParams";
import {getHistoryParams, isTaskChangeable} from "../../../utils/common";
import TaskChanger from "../../TaskChanger/TaskChanger";
import {FileSelectionType} from "../../../interfaces/store/actions/payload/layout";

interface StateProps {
    tasks: TaskEntity[]
    activeTask: Nullable<Task>
    fileSelectionType: Nullable<FileSelectionType>
    isMaskVisible: boolean
    statusStats: Nullable<StatusStats>
    activeTaskEntity: Nullable<TaskEntity>
    allTasksAreReady: boolean
}

interface DispatchProps {
    selectTaskInWizard(payload: string): SelectTaskInWizardAction

    updateTaskStatus(payload: Nullable<TaskEntity>): UpdateTaskStatusAction

    setTaskMaskData(payload: TaskMaskData): SetTaskMaskDataAction
}

type Props = StateProps & DispatchProps;

class TasksWizard extends React.Component<Props> {
    constructor(props: Props) {
        super(props);

        this.selectEditableIfExists = this.selectEditableIfExists.bind(this);
        this.renderTaskWizardBlock = this.renderTaskWizardBlock.bind(this);
    }

    componentDidUpdate(): void {
        const {
            activeTask, tasks, activeTaskEntity, allTasksAreReady, fileSelectionType,
        }: Props = this.props;
        const historyParams: Nullable<HistoryParams> = getHistoryParams();

        if (historyParams) {
            const {taskId} = historyParams;
            if (taskId && tasks.length && (!activeTask || taskId !== activeTask.id)) {
                this.props.selectTaskInWizard(taskId);
            } else if (!activeTask && !allTasksAreReady) {
                this.selectEditableIfExists();
            } else if (activeTaskEntity && fileSelectionType === FileSelectionType.SINGLE) {
                this.props.updateTaskStatus(activeTaskEntity);
            } else if (allTasksAreReady) {
                this.props.setTaskMaskData({visible: false});
            }
        }
    }

    selectEditableIfExists() {
        const {tasks}: Props = this.props;
        const editableTask: Nullable<TaskEntity> = tasks.find(
            (task: TaskEntity): boolean => isTaskChangeable(task.status),
        );
        if (editableTask) {
            this.props.selectTaskInWizard(editableTask.payload);
        }
    }

    renderTaskWizardBlock(currentTask: TaskEntity): React.ReactNode {
        return (
            <div className="df-tasks-wizard">
                <TaskChanger/>
                <TaskContainer task={currentTask}/>
            </div>
        );
    }

    render(): ReactNode {
        const {
            fileSelectionType, isMaskVisible, activeTaskEntity,
        }: Props = this.props;

        let wizardContent: React.ReactNode = null;
        if (fileSelectionType === FileSelectionType.BULK) {
            wizardContent = <FilePicker/>;
        } else if (fileSelectionType === FileSelectionType.SINGLE && activeTaskEntity) {
            wizardContent = this.renderTaskWizardBlock(activeTaskEntity);
        } else {
            // TODO Vladica: create error view, displayed when task data is not available
        }

        return (
            <Spin
                spinning={isMaskVisible}
                size="large"
                wrapperClassName="df-task-mask"
            >
                {wizardContent}
            </Spin>
        );
    }
}

function mapStateToProps({
    job: {
        activeTask,
        tasks,
        taskMaskData: {visible},
        jobPhase,
    },
    layout: {
        content: {fileSelectionType},
    },
}: StoreState): StateProps {
    const activeTaskEntity: Nullable<TaskEntity> = tasks.find(
        (task: TaskEntity): boolean => task.payload === activeTask?.id,
    );
    const readyTasks: TaskEntity[] = tasks.filter((task: TaskEntity) => task.status === Status.READY);
    return {
        activeTask,
        tasks,
        fileSelectionType,
        isMaskVisible: visible,
        statusStats: jobPhase?.phaseStats,
        activeTaskEntity,
        allTasksAreReady: tasks.length > 0 && tasks.length === readyTasks.length,
    };
}

const mapDispatchToProps: DispatchProps = {
    selectTaskInWizard,
    updateTaskStatus,
    setTaskMaskData,
};

export default connect(mapStateToProps, mapDispatchToProps)(TasksWizard);
